// Global notification listener — fires browser/sound notifications for
// SMS/MMS arriving in any of the user's servers, regardless of which
// channel page they're currently on. Calls (incoming-call popup +
// ringtone) are handled by call-session.ts; this module just makes sure
// the call subscription is active globally too.
//
// Wired from app.ts: installGlobalNotifier() once after auth+servers
// load, then refreshGlobalSubscriptions() whenever the server list
// changes (hub-servers-changed event in app.ts).

import { realtime, channelTopic, type RealtimeEvent } from '../realtime';
import { state } from '../state';
import { notify } from './notifications';
import { ensureCallSubscription } from './call-session';

let installed = false;
let subscribed = new Set<string>();

interface SmsLikeMessage {
  author_id?: string;
  content?: string;
  server_id?: string;
}

export function installGlobalNotifier(): void {
  if (installed) return;
  installed = true;
  realtime.connect();
  realtime.on((ev: RealtimeEvent) => {
    if (ev.type !== 'message_created') return;
    const topic = ev.channel_id;
    const isSms = topic.endsWith(':sms');
    const isMms = topic.endsWith(':mms');
    if (!isSms && !isMms) return;
    const msg = ev.message as SmsLikeMessage;
    if (msg.author_id && msg.author_id === state.ownerId) return;
    const text = (msg.content ?? '').trim();
    const preview = text.length > 80 ? text.slice(0, 80) + '...' : text;
    notify({
      kind: isSms ? 'sms' : 'mms',
      title: isSms ? 'New SMS' : 'New MMS',
      body: preview || (isSms ? 'New text message' : 'Picture message'),
      serverId: msg.server_id,
    });
  });
  refreshGlobalSubscriptions();
}

export function refreshGlobalSubscriptions(): void {
  if (!installed) return;
  // Subscribe to every server wildcard ("<server_id>:*") so realtime delivers
  // message_created for ALL channels, not just the one on screen — this powers
  // cross-channel web notifications (app.ts → notifyForMessage). subscribeServer
  // is idempotent. The explicit sms/mms channel subs below stay so the SMS/MMS
  // popups keep their specialised titles (notifyForMessage skips sms/mms to
  // avoid a double notification).
  for (const s of state.servers) realtime.subscribeServer(s.id);
  const want = new Set<string>();
  for (const s of state.servers) {
    want.add(channelTopic(s.id, 'sms'));
    want.add(channelTopic(s.id, 'mms'));
  }
  for (const t of subscribed) {
    if (!want.has(t)) realtime.unsubscribeChannel(t);
  }
  for (const t of want) {
    if (!subscribed.has(t)) realtime.subscribeChannel(t);
  }
  subscribed = want;
  ensureCallSubscription();
}
